import { FC, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { PerCardInfo } from '../../../../components/custom_components/CardInfo'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import { managerConfirmPreparation } from '../../../../graphql/appraisal-flow/hooks/useManagerConfirmPreparation'
import { MeetingStep, MeetingStepStatus, MeetingStepViewId } from '../../../../types'
import { MeetingStepDefaultProps } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { AppraisalFlowContext } from '../shared/AppraisalFlowProvider'
import { addOrUpdateFeedback } from '../shared/AppraisalMeetingService'
import AppraisalPageDescription from '../shared/AppraisalPageDescription'
import { MeetingConfirmBtn } from '../shared/MeetingStepButton'
import { MeetingModalReviewer } from '../shared/modals/MeetingModalReviewer'
import { generateStepName, getIncompleteStepReason } from '../shared/util'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
import RichTextBox from '../../../../components/custom_components/RichTextBox'

const ManagerMeetingStepNotes: FC<MeetingStepDefaultProps> = ({ meeting, backBtn }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const { meetingTypeSteps } = useContext(AppraisalFlowContext)
  const [open, changeOpen] = useState(false)
  const [valid, setValid] = useState(true)
  const [feedbackState, setFeedbackState] = useState<string | undefined>(
    meeting.appraisal && meeting.appraisal.reviewerGeneralFeedback ? meeting.appraisal.reviewerGeneralFeedback : undefined,
  )

  const updateFeedback = async () => {
    if (feedbackState) {
      try {
        await addOrUpdateFeedback(meeting.id, feedbackState)
        setValid(true)
      } catch (err) {
        showNotification({
          type: 'error',
          message: t(`appraisalMeeting.stepNotes.notifications.error.title`),
          description: t(`appraisalMeeting.stepNotes.notifications.error.description`),
        })
        setValid(false)
      }
    }
  }

  const updateMeetingAndRedirect = async () => {
    try {
      await managerConfirmPreparation(meeting.id)
      history.push('/meetings')
    } catch (err) {
      showNotification({
        type: 'error',
        message: t(`appraisalMeeting.stepNotes.notifications.error.title`),
        description: t(`appraisalMeeting.stepNotes.notifications.error.description`),
      })
      setValid(false)
    }
  }

  const incompleteMeetingSteps: MeetingStep[] = meetingTypeSteps
    //mapping before filtering to set right positions because positions of some steps recovered from meeting are not right
    .map((step, index) => ({ ...step, position: index + 1 }))
    .filter((step) => step.viewId !== MeetingStepViewId.Notes && step.status !== MeetingStepStatus.Done)

  const renderReason = ({ position, viewId, id, status }: MeetingStep) => (
    <li key={id}>
      <PerText mt="xxxxs">
        {t('appraisalMeeting.stepNotes.infoCard.incompleteStep', {
          position,
          stepName: generateStepName(viewId, meeting.type),
          reason: getIncompleteStepReason(status),
        })}
      </PerText>
    </li>
  )

  return (
    <div data-e2e-manager-meeting-step-notes>
      <MeetingModalReviewer
        visible={open && valid}
        onCancel={() => changeOpen(false)}
        onConfirm={() => {
          updateMeetingAndRedirect()
        }}
      />
      <MeetingBackground>
        <PerText appearance="subtitle" color="primary" mb="m">
          {t('appraisalMeeting.stepNotes.heading')}
        </PerText>
        {incompleteMeetingSteps.length > 0 && (
          <>
            <PerCardInfo
              heading={t('appraisalMeeting.stepNotes.infoCard.heading')}
              description={t('appraisalMeeting.stepNotes.infoCard.description')}
              childrenMarginTop="xxxxs"
              icon={{ name: 'warning_fill' }}
            >
              <PerText appearance="info" ml="xxxl">
                <ul>{incompleteMeetingSteps.map(renderReason)}</ul>
              </PerText>
            </PerCardInfo>
            <PerSpacer mb="xl" />
          </>
        )}
        <AppraisalPageDescription description={t('appraisalMeeting.stepNotes.pageDescriptionPreparation')} />

        <RichTextBox
          placeholder={t('appraisalMeeting.stepNotes.placeholder')}
          onBlur={updateFeedback}
          text={feedbackState || ''}
          onChange={(text) => {
            setFeedbackState(text)
          }}
        />
      </MeetingBackground>

      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        <MeetingConfirmBtn
          onClick={async () => {
            // Manually trigger feedback save because onBlur is not reliably fired
            // when the user clicks the button while the RichTextBox is still focused.
            await updateFeedback()
            changeOpen(true)
          }}
          text={t('appraisalMeeting.stepNotes.confirmBtn')}
        />{' '}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.Notes} stepStatus={MeetingStepStatus.Visited} />
    </div>
  )
}
export default ManagerMeetingStepNotes
